import { Entity, World } from "ecsy";
import { createEntity, getEntityById, getId } from "./entityFactory";
import { ArrayVec2 } from "./synchronization";
import { vec2 } from "@tlaukkan/tsm";
import { ShopConstants } from "./constants";
import { BloodCell, ColliderShape, Cytokine, ElementInfo, Ownership, Transform } from "./components";
import { queryBox, queryRadius, testOverlap } from "./box2dUtils";
import { EntityParameters, lookupGameElementShapeAndSize } from "./gameElement";
import { ElementDisplayInfo, StatName } from "./elementDisplayInfo";
import { cellLocations } from "./generated/assetLocations";
import { AssetLocation, degToRad, pixelsToMeters, radToDeg, vecToStr } from "./utils";
import { Game } from "./game";
import { GameElement } from "./gameElementTypes";
import { canCombine, hasRoom, Player } from "./player";


export type CompositionElement = {
	type: GameElement,
	position: ArrayVec2,
	id: number
	rotation: number,
	combined: number,
	entityParameters?: EntityParameters,
	promoted?: boolean,
	changedStats?: {
		[S in StatName]?: number
	}
}

export type Composition = Array<CompositionElement>;

export function getEntityPlayer(entity: Entity, game: Game) {
	const owner = entity.getComponent(Ownership);
	if (!owner) return null;
	const player = game.players.find(player => player.id === owner.playerId);
	return player || null;
}

export function getElementStat(type: GameElement, promoted: boolean, stat: StatName) {
	const info = ElementDisplayInfo.get(type);
	if (info) {
		if (promoted && info.veterenStats && info.veterenStats[stat] !== undefined) {
			return info.veterenStats[stat]!;
		}
		else if (info.stats[stat] !== undefined) {
			return info.stats[stat]!;
		}
		else {
			//console.error(`Element had no matching stat ${stat}. Element: ${GameElement[type]}`);
			return 0;
		}
	}
	return 0;
}

export function getElementConstant(type: GameElement, promoted: boolean, name: string, defaultValue = 0) {
	const info = ElementDisplayInfo.get(type);
	if (info) {
		if (promoted && info.veterenConstants && info.veterenConstants[name] !== undefined) {
			return info.veterenConstants[name]!;
		}
		else if (info.constants && info.constants[name] !== undefined) {
			return info.constants[name]!;
		}
		else {
			return defaultValue;
		}
	}
}

export function getCompStat(el: CompositionElement, stat: StatName) {
	let base = getElementStat(el.type, el.promoted || false, stat);
	const added = el.changedStats?.[stat] || 0;
	return base + added;
}

export function getAllElementStats(el: CompositionElement, promoted = el.promoted) {
	const info = ElementDisplayInfo.get(el.type);
	const releventStats = new Set<string>();
	if (info) {
		for (const stat in info.stats) {
			releventStats.add(stat);
		}
		if (promoted) {
			for (const stat in info.veterenStats) {
				releventStats.add(stat);
			}
		}
		for (const stat in el.changedStats) {
			releventStats.add(stat);
		}
	}
	let stats: { [S in StatName]?: number } = {};
	Array.from(releventStats).forEach(stat => {
		stats[stat as StatName] = getCompStat(el, stat as StatName);
	});
	return stats;
}

export function addElementStat(el: CompositionElement, stat: StatName, val: number) {
	if (!el.changedStats) el.changedStats = {};
	const current = el.changedStats[stat] || 0;
	const newVal = current + val;
	el.changedStats[stat] = newVal;
}

export function setElementStat(el: CompositionElement, stat: StatName, val: number) {
	if (!el.changedStats) el.changedStats = {};
	const base = getElementStat(el.type, el.promoted || false, stat);
	el.changedStats[stat] = val - base;
}

export function instantiateComp(world: World, comp: Composition, playerId?: number, unsynced = false) {
	for (const element of comp) {
		instantiateCompElement(element, playerId, unsynced);
	}
}

export function instantiateCompElement(element: CompositionElement, playerId?: number, unsynced = false) {
	const position = element.position;
	const rotation = element.rotation;
	const promoted = element.promoted || false;
	playerId = playerId !== undefined ? playerId : -1;
	createEntity({
		type: element.type,
		position,
		promoted,
		rotation,
		playerId,
		sourceCompElement: element,
		unsynced,
		entityParameters: element.entityParameters
	});
}

/**TODO: This is pretty inefficient lookup, could easily store all sizes somwewhere or memoize this */
export function lookupEntitySize(type: GameElement) {
	const info = ElementDisplayInfo.get(type);
	const assetName = info ? info.asset : "bloodcell";
	const location = cellLocations[assetName] as AssetLocation;
	const width = location.width;
	const height = location.height;
	const w = pixelsToMeters(width);
	const h = height ? pixelsToMeters(height) : w;
	return new vec2([w, h]);
}

export function findOverlapInWorld(
	transform: { size: vec2, rotation: number, position: vec2, shape: ColliderShape, id?: number },
	overrideTransforms?: Array<{ id: number, position: vec2, rotation: number }>) {

	const checkNotSelf = (entity: Entity) => entity.getComponent(Transform) !== transform && !(transform.id && getId(entity) === transform.id);

	let overlaps: Array<Entity>;
	if (transform.shape === ColliderShape.Circle) {
		overlaps = queryRadius(transform.position, transform.size.x / 2, checkNotSelf);
	}
	else {
		const rotation = transform.rotation;
		overlaps = queryBox(transform.position, transform.size, rotation, checkNotSelf);
	}

	//Group moves: the world still has every group member at its old spot, so check them at their new one
	if (overrideTransforms && overrideTransforms.length > 0) {
		const overlapsAt = (entity: Entity, override: { position: vec2, rotation: number }) => {
			const otherTransform = entity.getComponent(Transform)!;
			return testOverlap(
				override.position, otherTransform.size, override.rotation, otherTransform.shape,
				transform.position, transform.size, transform.rotation, transform.shape
			);
		};
		overlaps = overlaps.filter(overlappingEntity => {
			const overlapId = getId(overlappingEntity);
			const matchingOverride = overrideTransforms.find(o => o.id == overlapId);
			if (!matchingOverride) return true;
			return overlapsAt(overlappingEntity, matchingOverride);
		});
		//Check for any group members that overlap each other at new positions. Should be impossible with normal rules but client could cheat
		for (const override of overrideTransforms) {
			const entity = getEntityById(override.id);
			if (!entity || !checkNotSelf(entity) || overlaps.includes(entity)) continue;
			if (overlapsAt(entity, override)) {
				overlaps.push(entity);
			}
		}
	}

	return overlaps;
}

export function findOverlapInComposition(
	transform: { size: vec2, rotation: number, position: vec2, shape: ColliderShape },
	id: number,
	comp: Composition,
	overrideTransforms = new Array<{ id: number, position: vec2, rotation: number }>()
) {
	const overlaps: Array<CompositionElement> = [];
	for (const el of comp) {
		if (el.id == id) continue; //skip self
		let target: { position: ArrayVec2, rotation: number } = el;
		const override = overrideTransforms.find(item => item.id === el.id);
		if (override) { target = { position: override.position.xy, rotation: override.rotation }; }

		const { shapeType, size } = lookupGameElementShapeAndSize(el.type);
		const pos = new vec2(target.position);
		const doesOverlap = testOverlap(
			transform.position, transform.size, transform.rotation, transform.shape,
			pos, size, target.rotation, shapeType
		);
		if (doesOverlap) {
			overlaps.push(el);
		}
	}
	return overlaps;
}

export type ValidatePlacementOrCombintationOptions = {
	disallowCombine?: boolean,
	skipBloodCellOverlap?: boolean,
	detectedCombinationTargets?: Array<number>,
	detectedOverlaps?: Array<number>,
	overrideTransforms?: Array<{ id: number, position: vec2, rotation: number }> //IMPLEMENT THISs
}

export function validatePlacementOrCombination(
	player: Player,
	transform: { size: vec2, rotation: number, position: vec2, shape: ColliderShape, id?: number },
	cell: CompositionElement | { type: GameElement } | { type: GameElement, id: number },
	game: Game | null,
	options: ValidatePlacementOrCombintationOptions = {}
) {
	const spawnPoint = new vec2(player.spawnPoint);
	let overlaps: Array<Entity> | Array<CompositionElement> = [];
	if (game !== null) {
		overlaps = findOverlapInWorld(transform, options.overrideTransforms || []);
		if (options.skipBloodCellOverlap === true && (cell.type === GameElement.BLOODCELL || cell.type === GameElement.INFECTED_BLOODCELL)) {
			//If we are skipping blood cell overlap, we only want to check for immune cells and enemy cells
			overlaps = overlaps.filter(entity => !entity.hasComponent(BloodCell));
		}
	}
	else {
		const selfId = (cell as CompositionElement).id || -1;
		overlaps = findOverlapInComposition(transform, selfId, player.immuneCells, options.overrideTransforms || []);
		if (!options.skipBloodCellOverlap) {
			const bloodCellOverlaps = findOverlapInComposition(transform, selfId, player.hpCells, options.overrideTransforms || []);
			overlaps.push(...bloodCellOverlaps);
		}
	}
	let validCombination = false;
	let validPlacement = true;
	for (const entityOrCompEl of overlaps) {

		let overlapElement: CompositionElement;
		if ((entityOrCompEl as any).position === undefined) { //is a Entity
			const entity = entityOrCompEl as Entity;
			if (entity.hasComponent(Cytokine)) continue;
			const el = game!.getEntityCompElement(entity);
			if (!el) {
				console.warn(`validatePlacementOrCombination: Overlapping Entity ${entity.id} has no CompositionElement`);
				validPlacement = false; //overlaps something that is not a comp element 
				if (options.detectedOverlaps) { options.detectedOverlaps.push(getId(entity) || -1); }
				continue;
			}
			overlapElement = el;
		}
		else {
			overlapElement = entityOrCompEl as CompositionElement;
		}

		validPlacement = false; //overlaps something
		if (options.detectedOverlaps) { options.detectedOverlaps.push(overlapElement.id); }

		const isValidCombination = canCombine(player, overlapElement, cell);
		if (isValidCombination) {
			if (options.detectedCombinationTargets) options.detectedCombinationTargets.push(overlapElement.id);
			validCombination = true;
		}
	}
	if (options.disallowCombine) validCombination = false;

	const distanceFromSpawn = vec2.distance(transform.position, spawnPoint);
	if (distanceFromSpawn > (ShopConstants.PlacementRadius)) {
		validPlacement = false;
	}

	return validPlacement || validCombination;
}

export function shouldPromote(el: CompositionElement, promoteCount = ShopConstants.PromotionNumber) {
	if (el.combined >= (promoteCount - 1)) {
		return true;
	}
	return false;
}

export function roundPositionToCompStep(position: vec2, dest = new vec2()) {
	const step = ShopConstants.MovementStepMeters;
	dest.x = Math.round(position.x / step) * step;
	dest.y = Math.round(position.y / step) * step;
	return position.copy();
}

export function roundRotationToCompStep(rotation: number) {
	const step = ShopConstants.RotationStepDegrees;
	return degToRad(Math.round(radToDeg(rotation) / step) * step);
}

export function getInfectedCellSpawnAmount(cell: CompositionElement) {
	return (cell.entityParameters?.spawnAmount || 0) + (cell.entityParameters?.spawnBattleAddedAmount || 0);
}